import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  EditButton,
  DeleteButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  BooleanField,
  BooleanInput,
  Filter,
  TopToolbar,
  CreateButton,
  required,
  minValue,
  maxValue,
  Show,
  SimpleShowLayout,
  FunctionField,
  useRecordContext,
  BulkDeleteButton,
  BulkUpdateButton,
  DateInput,
  useInput,
  SimpleList,
  useNotify,
} from 'react-admin';
import { uploadImage } from '../utils/imageUpload';
import {
  Box,
  Chip,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Avatar,
  useMediaQuery,
  Theme,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  Preview as PreviewIcon,
  Star as StarIcon,
} from '@mui/icons-material';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// 배너 필터
const BannerFilter = (props: any) => (
  <Filter {...props}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextInput label="검색 (제목/URL)" source="q" alwaysOn fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="position"
          label="배너 위치"
          choices={[
            { id: 'MAIN_TOP', name: '메인 상단' },
            { id: 'CATEGORY_TOP', name: '카테고리 상단' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <BooleanInput source="isActive" label="활성 상태" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateInput source="startDate" label="시작일 이후" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DateInput source="endDate" label="종료일 이전" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <NumberInput source="minClickCount" label="최소 클릭수" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <NumberInput source="displayOrder" label="표시 순서" fullWidth />
      </Grid>
    </Grid>
  </Filter>
);

// 배너 상태 표시 컴포넌트
const BannerStatusField = (props: any) => {
  const { record: recordProp } = props;
  const recordContext = useRecordContext();
  const record = recordProp || recordContext;
  if (!record) return null;

  const now = new Date();
  const startDate = new Date(record.startDate);
  const endDate = new Date(record.endDate);

  let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = 'default';
  let label = '';

  if (record.isActive) {
    if (now < startDate) {
      color = 'info';
      label = '예약됨';
    } else if (now > endDate) {
      color = 'error';
      label = '만료됨';
    } else {
      color = 'success';
      label = '활성';
    }
  } else {
    color = 'default';
    label = '비활성';
  }

  return <Chip label={label} color={color} size="small" />;
};

// 배너 위치 표시 컴포넌트
const BannerPositionField = (props: any) => {
  const { record: recordProp } = props;
  const recordContext = useRecordContext();
  const record = recordProp || recordContext;
  if (!record) return null;

  const positionMap: { [key: string]: { label: string; color: "default" | "primary" | "secondary" } } = {
    MAIN_TOP: { label: '메인 상단', color: 'primary' },
    CATEGORY_TOP: { label: '카테고리 상단', color: 'secondary' },
  };

  const position = positionMap[record.position] || { label: record.position, color: 'default' };

  return <Chip label={position.label} color={position.color} size="small" />;
};

// 배너 성과 표시 컴포넌트
const BannerPerformanceField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Typography variant="body2">{record.clickCount?.toLocaleString() || 0}</Typography>
  );
};

// 배너 이미지 미리보기 컴포넌트
const BannerImagePreview = () => {
  const record = useRecordContext();
  if (!record || !record.imageUrl) return null;

  return (
    <Avatar
      src={record.imageUrl}
      alt={record.title}
      variant="rounded"
      sx={{ width: { xs: 50, md: 60 }, height: { xs: 33, md: 40 } }}
    />
  );
};

// 배너 표시 순서 컴포넌트
const BannerDisplayOrderField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <StarIcon color={record.displayOrder <= 5 ? 'primary' : 'disabled'} fontSize="small" />
      <Typography variant="body2">{record.displayOrder}</Typography>
    </Box>
  );
};

// 이미지 업로드 컴포넌트
const ImageUploadInput = ({ source, label, validate }: any) => {
  const { field } = useInput({ source, validate });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string>(field.value || '');
  const notify = useNotify();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify('파일 크기는 5MB 이하여야 합니다', { type: 'error' });
      return;
    }

    // 이미지 파일 타입 체크
    if (!file.type.startsWith('image/')) {
      notify('이미지 파일만 업로드 가능합니다', { type: 'error' });
      return;
    }

    try {
      setUploading(true);
      const imageUrl = await uploadImage(file, 'banners');
      field.onChange(imageUrl);
      setPreview(imageUrl);
      notify('이미지 업로드 완료', { type: 'success' });
    } catch (error: any) {
      notify(error.message || '이미지 업로드 실패', { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="image-upload-input"
        type="file"
        onChange={handleFileChange}
        disabled={uploading}
      />
      <label htmlFor="image-upload-input">
        <Button
          variant="contained"
          component="span"
          disabled={uploading}
        >
          {uploading ? '업로드 중...' : label || '이미지 선택'}
        </Button>
      </label>
      {preview && (
        <Box sx={{ mt: 2 }}>
          <img
            src={preview}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </Box>
      )}
    </Box>
  );
};

// 배너 목록 액션 버튼
const BannerListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// 배너 일괄 액션 버튼
const BannerBulkActions = () => (
  <>
    <BulkUpdateButton label="일괄 활성화" data={{ isActive: true }} />
    <BulkUpdateButton label="일괄 비활성화" data={{ isActive: false }} />
    <BulkDeleteButton />
  </>
);

// 드래그앤드롭으로 순서 변경 가능한 배너 목록 (향후 구현 예정)
// const DraggableBannerList = () => {
//   const [items, setItems] = useState<any[]>([]);
//   const dataProvider = useDataProvider();
//   const notify = useNotify();
//   const refresh = useRefresh();

//   React.useEffect(() => {
//     // 초기 데이터 로드 로직
//   }, []);

//   const handleDragEnd = async (result: any) => {
//     if (!result.destination) return;

//     const newItems = Array.from(items);
//     const [reorderedItem] = newItems.splice(result.source.index, 1);
//     newItems.splice(result.destination.index, 0, reorderedItem);

//     setItems(newItems);

//     try {
//       // 순서 업데이트 API 호출
//       await dataProvider.update('banners/reorder', {
//         id: reorderedItem.id,
//         data: { newOrder: result.destination.index },
//       });
//       notify('배너 순서가 변경되었습니다', { type: 'success' });
//       refresh();
//     } catch (error) {
//       notify('순서 변경 실패', { type: 'error' });
//       setItems(items); // 원복
//     }
//   };

//   return (
//     <DragDropContext onDragEnd={handleDragEnd}>
//       <Droppable droppableId="banners">
//         {(provided) => (
//           <div {...provided.droppableProps} ref={provided.innerRef}>
//             {items.map((item, index) => (
//               <Draggable key={item.id} draggableId={item.id} index={index}>
//                 {(provided) => (
//                   <Card
//                     ref={provided.innerRef}
//                     {...provided.draggableProps}
//                     sx={{ mb: 1, p: 1 }}
//                   >
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                       <div {...provided.dragHandleProps}>
//                         <DragIcon />
//                       </div>
//                       <BannerImagePreview />
//                       <Box sx={{ flexGrow: 1 }}>
//                         <Typography variant="subtitle1">{item.title}</Typography>
//                         <Typography variant="body2" color="textSecondary">
//                           {item.position} | 우선순위: {item.priority}
//                         </Typography>
//                       </Box>
//                       <BannerStatusField />
//                     </Box>
//                   </Card>
//                 )}
//               </Draggable>
//             ))}
//             {provided.placeholder}
//           </div>
//         )}
//       </Droppable>
//     </DragDropContext>
//   );
// };

// 배너 미리보기 다이얼로그
const BannerPreviewDialog = ({ open, onClose, banner }: any) => {
  if (!banner) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        배너 미리보기: {banner.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', p: { xs: 2, sm: 2.5, md: 3 } }}>
          <img
            src={banner.imageUrl}
            alt={banner.title}
            style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd' }}
          />
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>기본 정보</Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>위치: {banner.position}</Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>상태: {banner.isActive ? '활성' : '비활성'}</Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>표시 순서: {banner.displayOrder}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>성과</Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>클릭수: {banner.clickCount?.toLocaleString() || 0}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        {banner.linkUrl && (
          <Button
            variant="contained"
            onClick={() => window.open(banner.linkUrl, '_blank')}
            startIcon={<LaunchIcon />}
          >
            링크 이동
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// 배너 목록
export const BannerList = () => {
  return (
    <>
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          배너
        </Typography>
      </Box>
      <List
        title="배너"
        filters={<BannerFilter />}
        actions={<BannerListActions />}
        bulkActionButtons={<BannerBulkActions />}
        sort={{ field: 'displayOrder', order: 'ASC' }}
      >
        {useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')) ? (
          <SimpleList
            primaryText={(record) => record.title}
            secondaryText={(record) => (
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <BannerPositionField record={record} />
                <BannerStatusField record={record} />
              </Box>
            )}
            tertiaryText={(record) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption">
                  {new Date(record.endDate).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  클릭: {record.clickCount?.toLocaleString() || 0}
                </Typography>
              </Box>
            )}
            leftAvatar={(record) => (
              <Avatar
                src={record.imageUrl}
                variant="rounded"
                sx={{ width: 50, height: 50 }}
              />
            )}
            linkType="edit"
          />
        ) : (
          <Datagrid rowClick="edit" sx={{
            '& .RaDatagrid-headerCell': {
              fontWeight: 600,
              textAlign: 'center',
            },
            '& .RaDatagrid-rowCell': {
              verticalAlign: 'middle',
              textAlign: 'center',
            }
          }}>
            <BannerImagePreview />
            <TextField source="title" label="제목" />
            <BannerStatusField label="상태" />
            <BannerDisplayOrderField label="표시 순서" />
            <FunctionField
              label="노출 기간"
              render={(record: any) =>
                `${new Date(record.startDate).toLocaleDateString()} ~ ${new Date(record.endDate).toLocaleDateString()}`
              }
            />
            <TextField source="linkUrl" label="링크 URL" />
            <BannerPerformanceField label="클릭수" />
            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 배너 생성
export const BannerCreate = () => (
  <Create>
    <SimpleForm defaultValues={{ position: 'MAIN_TOP', backgroundColor: '#f3f4f6', textColor: '#1f2937' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextInput source="title" label="배너 제목" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="position"
            label="배너 위치"
            choices={[
              { id: 'MAIN_TOP', name: '메인 상단' },
              { id: 'CATEGORY_TOP', name: '카테고리 상단' },
            ]}
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="subtitle" label="부제목 (카테고리 배너용)" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>이미지 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <ImageUploadInput
            source="imageUrl"
            label="배너 이미지 업로드"
            validate={required()}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>색상 설정 (카테고리 배너용)</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="backgroundColor" label="배경색 (예: #FFE4C4)" fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="textColor" label="텍스트 색상 (예: #4A4A4A)" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>링크 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="linkUrl" label="링크 URL" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>스케줄링 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput source="startDate" label="노출 시작일" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput source="endDate" label="노출 종료일" validate={required()} fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>표시 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="displayOrder"
            label="표시 순서"
            defaultValue={10}
            validate={[required(), minValue(1), maxValue(100)]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BooleanInput
            source="isActive"
            label="활성 상태"
            defaultValue={false}
          />
        </Grid>
      </Grid>
    </SimpleForm>
  </Create>
);

// 배너 수정
export const BannerEdit = () => (
  <Edit>
    <SimpleForm>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextInput source="title" label="배너 제목" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="position"
            label="배너 위치"
            choices={[
              { id: 'MAIN_TOP', name: '메인 상단' },
              { id: 'CATEGORY_TOP', name: '카테고리 상단' },
            ]}
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="subtitle" label="부제목 (카테고리 배너용)" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>이미지 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <ImageUploadInput
            source="imageUrl"
            label="배너 이미지 업로드"
            validate={required()}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>색상 설정 (카테고리 배너용)</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="backgroundColor" label="배경색 (예: #FFE4C4)" fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="textColor" label="텍스트 색상 (예: #4A4A4A)" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>링크 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="linkUrl" label="링크 URL" fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>스케줄링 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput source="startDate" label="노출 시작일" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput source="endDate" label="노출 종료일" validate={required()} fullWidth />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>표시 설정</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <NumberInput
            source="displayOrder"
            label="표시 순서"
            validate={[required(), minValue(1), maxValue(100)]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BooleanInput
            source="isActive"
            label="활성 상태"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>통계</Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>클릭수</Typography>
                <NumberField source="clickCount" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>생성일</Typography>
                <DateField source="createdAt" showTime />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>수정일</Typography>
                <DateField source="updatedAt" showTime />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </SimpleForm>
  </Edit>
);

// 배너 상세 보기
export const BannerShow = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const record = useRecordContext();

  return (
    <>
      <Show>
        <SimpleShowLayout sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>기본 정보</Typography>
              <TextField source="id" label="ID" />
              <TextField source="title" label="배너 제목" />
              <BannerPositionField label="배너 위치" />
              <BannerStatusField label="상태" />
              <BannerDisplayOrderField label="표시 순서" />
              <BooleanField source="isActive" label="활성 상태" />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>이미지 미리보기</Typography>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {record?.imageUrl && (
                  <img
                    src={record.imageUrl}
                    alt={record.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                )}
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setPreviewOpen(true)}
                    startIcon={<PreviewIcon />}
                  >
                    전체 미리보기
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>링크 정보</Typography>
              <TextField source="linkUrl" label="링크 URL" />
              {record?.linkUrl && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(record.linkUrl, '_blank')}
                    startIcon={<LaunchIcon />}
                  >
                    링크 이동
                  </Button>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>노출 기간</Typography>
              <DateField source="startDate" label="시작일" showTime />
              <DateField source="endDate" label="종료일" showTime />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>성과 통계</Typography>
              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>클릭수</Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      <NumberField source="clickCount" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>평균 일일 클릭</Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      <FunctionField
                        render={(record: any) => {
                          const days = Math.ceil(
                            (new Date(record.endDate).getTime() - new Date(record.startDate).getTime())
                            / (1000 * 60 * 60 * 24)
                          );
                          return days > 0 ? Math.round(record.clickCount / days) : 0;
                        }}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>기타 정보</Typography>
              <DateField source="createdAt" label="생성일" showTime />
              <DateField source="updatedAt" label="수정일" showTime />
            </Grid>
          </Grid>
        </SimpleShowLayout>
      </Show>

      <BannerPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        banner={record}
      />
    </>
  );
};